/**
 * One hook per section endpoint the editors write to — each an OUTBOX write
 * since `D194`. The cached section changes the moment the person taps Save
 * (`data/outbox-optimistic.ts`), the write is on disk before the sheet is
 * told it saved, and the server hears it when it can: at once online, after
 * the network comes back otherwise, and after a relaunch if the app was
 * closed first. Every one of these `PUT`s replaces the row, so the outbox's
 * resend after a lost answer writes the same thing again.
 *
 * A refusal is no longer the sheet's to show: the sheet has closed. It lands
 * in the outbox's quarantine (`offline.*`), and the effect drawn for it is
 * taken back (the overlay invalidates what it touched).
 *
 * The saved confirmation is NOT here: the editor reports `onSaved(editId)`
 * and T51 flashes the row (`p-saved`), because the row is what the person
 * looks at after the sheet closes.
 */
import { deleteAccountMutation } from "@repo/client/data/me";
import type { SetDisplayNameInput } from "@repo/contract/iam";
import type { NotificationPreferences } from "@repo/contract/notifications";
import type {
  EquipmentAvailabilityInput,
  InjuryListInput,
  MeasurementInput,
  MeasurementListOutput,
  ParqInput,
  ProfileInput,
  TrainingConfigInput,
} from "@repo/contract/training";
import { useCallback, useMemo } from "react";
import {
  measurementAdd,
  measurementDelete,
  type SectionKind,
  sectionPut,
  useOutboxWrite,
} from "../../../data/account-writes";
import { useApi } from "../../../data/api";
import { useDataMutation } from "../../../data/query-client";
import type { MutateCallbacks, Mutator } from "./editor-shell";

/** A `Mutator` over one outbox kind: queue, then answer `output(payload)`. */
function useOutboxMutator<P, O>(
  entryOf: (payload: P) => Parameters<ReturnType<typeof useOutboxWrite>>[0],
  output: (payload: P) => O
): Mutator<P, O> & { mutateAsync(payload: P): Promise<O> } {
  const write = useOutboxWrite();
  const mutateAsync = useCallback(
    async (payload: P) => {
      await write(entryOf(payload));
      return output(payload);
    },
    [entryOf, output, write]
  );
  const mutate = useCallback(
    (payload: P, options?: MutateCallbacks<O>) => {
      mutateAsync(payload).then(
        (answer) => options?.onSuccess?.(answer),
        (error: unknown) =>
          options?.onError?.(
            error instanceof Error ? error : new Error(String(error))
          )
      );
    },
    [mutateAsync]
  );
  return useMemo(
    () => ({ isPending: false, mutate, mutateAsync }),
    [mutate, mutateAsync]
  );
}

/** The `PUT`s whose answer nobody reads: the payload stands in for it. */
function useSectionPut<P>(kind: SectionKind) {
  const entryOf = useCallback(
    (payload: P) => sectionPut(kind, payload),
    [kind]
  );
  return useOutboxMutator<P, P>(entryOf, echo);
}

const echo = <P>(payload: P): P => payload;

export function useProfileMutation() {
  return useSectionPut<ProfileInput>("profile.put");
}

/** `level` is derived by the server from `experience`; the cached one stays
 *  until the read after the write brings the new one. */
export function useTrainingConfigMutation() {
  return useSectionPut<TrainingConfigInput>("training-config.put");
}

export function useEquipmentMutation() {
  return useSectionPut<EquipmentAvailabilityInput>("equipment.put");
}

export function useInjuriesMutation() {
  return useSectionPut<InjuryListInput>("injuries.put");
}

/** `medicalNoticeShown` is the server's rule (D98) and is not guessed. */
export function useParqMutation() {
  return useSectionPut<ParqInput>("parq.put");
}

export function useNotificationPreferencesMutation() {
  return useSectionPut<NotificationPreferences>("notification-prefs.put");
}

export function useDisplayNameMutation() {
  return useSectionPut<SetDisplayNameInput>("me.display-name.put");
}

/** What a weigh-in answers before the server has: enough for its toast and
 *  its undo, which names the attempt rather than a row id it has not got. */
export interface MeasurementQueued {
  readonly idempotencyKey: string;
  readonly value: number;
}

export function useRecordMeasurementMutation() {
  const entryOf = useCallback(
    (payload: MeasurementInput) =>
      measurementAdd({
        ...payload,
        idempotencyKey: payload.idempotencyKey ?? "",
      }),
    []
  );
  const output = useCallback(
    (payload: MeasurementInput): MeasurementQueued => ({
      idempotencyKey: payload.idempotencyKey ?? "",
      value: payload.value,
    }),
    []
  );
  return useOutboxMutator(entryOf, output);
}

/**
 * Undo of a weigh-in: a `DELETE` of the row just written, never a second
 * `POST` (the series is append-only). It rides the add's stream, so it goes
 * after the add has answered with the row's id; `previousLatest` is what the
 * card shows again meanwhile.
 */
export function useDeleteMeasurementMutation() {
  const write = useOutboxWrite();
  return useCallback(
    (input: {
      readonly idempotencyKey: string;
      readonly previousLatest: MeasurementListOutput["latest"];
    }) => write(measurementDelete(input)),
    [write]
  );
}

/** `DELETE /auth/account`. The caller discards the session on success. */
export function useDeleteAccountMutation() {
  const api = useApi();
  return useDataMutation(deleteAccountMutation(api));
}
